"use client";
import { useState, useEffect, useCallback, useMemo } from "react";
import dynamic from "next/dynamic";
import Image from "next/image";
import { useSearchParams, usePathname } from "next/navigation";
import {
    ARROW_CLASS,
    ARROW_ROTATE,
    getPathSpecificCountryData,
} from "./constants.js";
import { DownArrow } from "../../../public/Theme";
import { getFlagEmoji } from "../../../utils/countryUtils";
import { countriesWithRate } from "../../../public/Constants/CountriesList";
const DropDown = dynamic(() => import("./DropDown"));
import { changeCurrency } from "../../../lib/Country/action";
import useCountryStore from "../../../lib/Store/useCountryStore";

const CurrencySelectorDropdown = ({ userCountry, userData }) => {
    const searchParams = useSearchParams();
    const pathname = usePathname();
    const isSaudia = searchParams.get("sa");
    const [value, setValue] = useState(false);
    const [visible, setVisible] = useState(false);
    const { setZustandCountry } = useCountryStore();

    const pathSpecificCountryData = useMemo(
        () => getPathSpecificCountryData(pathname),
        [pathname]
    );

    // Function to handle keydown events for keyboard accessibility
    const handleKeyDown = (event) => {
        if (event?.key === "Enter" || event?.key === " ") {
            setValue((prev) => !prev);
        } else if (event?.key === "Escape") {
            setValue(false);
        }
    };

    // Memoize the function to fetch user location, so it doesn't re-run unnecessarily
    const getUserLocation = useCallback(async () => {
        if (!userCountry && !pathSpecificCountryData) {
            let result = {
                country_name: "United Arab Emirates",
                country_code: "AE",
            };
            try {
                const response = await fetch(
                    `${process.env.BACKEND_URL}api/v1/getLocation/?key=${process.env.IP2LOCATION_API_KEY}`
                );
                if (response.ok) {
                    result = await response.json();
                }
            } catch (e) {
                console.log(e);
            }
            const { country_code } = result;
            setCountry(country_code);
            setValue(false);
        } else {
            // ✅ Prefer path-specific flag over userCountry
            setCountry(pathSpecificCountryData?.flag || userCountry?.flag);
        }
    }, [userCountry, pathSpecificCountryData]);

    // Memoize the function that sets the country
    const setCountry = useCallback((country_code) => {
        const filterData = countriesWithRate()?.find(
            (country) => country?.iso_alpha_2 === country_code
        );
        if (!filterData) return;

        const data = {
            country: filterData?.country,
            flag: filterData?.iso_alpha_2,
            currency: filterData?.code,
            price: filterData?.subscription_rate,
        };
        setZustandCountry(data);
        changeCurrency(data);
    }, []);

    useEffect(() => {
        if (userData?.payment_status === "paid") setVisible(false);
        else if (pathname === "/checkout/") {
            const step = searchParams.get("step");
            if (step === "address") setVisible(true);
            else setVisible(false);
        } else setVisible(true);
    }, [pathname, searchParams, userData]);

    // Run effect based on certain conditions
    useEffect(() => {
        if (userData?.detected_signup_country) {
            setCountry(userData.detected_signup_country);
        } else if (isSaudia) {
            setVisible(true);
            setCountry("SA");
        } else {
            setVisible(true);
            getUserLocation(); // Fetch user location if no userCountry or pathSpecificCountryData
        }
    }, [userData, isSaudia, pathSpecificCountryData, setCountry]);

    return (
        <div className="relative w-[60px]">
            <button
                className="border border-solid border-white cursor-pointer flex gap-x-2 items-center text-xs lg:text-sm font-medium h-8 lg:h-10 min-w-fit"
                onClick={() => setValue((prev) => !prev)}
                disabled={!visible}
                onKeyDown={handleKeyDown}
                aria-expanded={value}
                aria-controls="currency-dropdown"
            >
                <span
                    className="inline-flex items-center justify-center w-8 h-8 text-xl"
                    aria-label={`Flag of ${pathSpecificCountryData?.country || userCountry?.country}`}
                >
                    {getFlagEmoji(
                        pathSpecificCountryData?.flag || userCountry?.flag
                    )}
                </span>

                {visible && (
                    <Image
                        width={0}
                        height={0}
                        className={ARROW_CLASS + (value ? ARROW_ROTATE : "")}
                        src={DownArrow}
                        alt="Chevron"
                    />
                )}
            </button>
            {value && (
                <DropDown userCountry={userCountry} setValue={setValue} />
            )}
        </div>
    );
};

export default CurrencySelectorDropdown;
